<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMultiSelectLabel } from 'dashboard/composables/useMultiSelectLabel';

const props = defineProps({
  // options: [{ value, label, icon?, dot?, avatar? }]
  options: { type: Array, default: () => [] },
  modelValue: { type: [String, Number, Array, null], default: null },
  multiple: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  // permite uma opção "vazia"/todos no topo (seleção única)
  clearable: { type: Boolean, default: false },
  clearLabel: { type: String, default: '' },
  compact: { type: Boolean, default: false },
  small: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const open = ref(false);
const search = ref('');
const highlight = ref(0);
const triggerRef = ref(null);
const dropdownStyle = ref({});

const selectedArray = computed(() =>
  props.multiple ? props.modelValue || [] : []
);

function isSelected(value) {
  return props.multiple
    ? selectedArray.value.includes(value)
    : props.modelValue === value;
}

const filteredOptions = computed(() => {
  const q = search.value.toLowerCase();
  if (!q) return props.options;
  return props.options
    .filter(o => o.label.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
    );
});

const navigable = computed(() => {
  const items = filteredOptions.value.map(o => o.value);
  if (props.clearable && !search.value) return ['__clear__', ...items];
  return items;
});

const multipleSelectedNames = computed(() =>
  selectedArray.value.map(
    v => props.options.find(o => o.value === v)?.label ?? '…'
  )
);
const multipleFitLabel = useMultiSelectLabel(triggerRef, multipleSelectedNames);

const triggerLabel = computed(() => {
  if (props.multiple) {
    return multipleFitLabel.value || props.placeholder;
  }
  if (props.modelValue === null || props.modelValue === '') {
    return props.placeholder;
  }
  return (
    props.options.find(o => o.value === props.modelValue)?.label ??
    props.placeholder
  );
});

const selectedOptions = computed(() =>
  selectedArray.value
    .map(v => props.options.find(o => o.value === v))
    .filter(Boolean)
);

function chipStyle(color) {
  if (!color) return {};
  return { backgroundColor: `${color}22`, color };
}

const selectedSingleColor = computed(() => {
  if (props.multiple) return null;
  return props.options.find(o => o.value === props.modelValue)?.color ?? null;
});

const isPlaceholder = computed(() =>
  props.multiple
    ? selectedArray.value.length === 0
    : props.modelValue === null || props.modelValue === ''
);

function calcDropdownStyle() {
  const el = triggerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    minWidth: `${rect.width}px`,
    zIndex: 10020,
  };
}

function close() {
  open.value = false;
  search.value = '';
  highlight.value = 0;
}

function toggleOpen() {
  if (open.value) {
    close();
    return;
  }
  calcDropdownStyle();
  open.value = true;
  highlight.value = 0;
}

function selectValue(value) {
  if (value === '__clear__') {
    emit('update:modelValue', props.multiple ? [] : null);
    close();
    return;
  }
  if (props.multiple) {
    const sel = [...selectedArray.value];
    const idx = sel.indexOf(value);
    if (idx === -1) sel.push(value);
    else sel.splice(idx, 1);
    emit('update:modelValue', sel);
  } else {
    emit('update:modelValue', value);
    close();
  }
}

function handleKeydown(e) {
  if (!open.value) return;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    highlight.value = Math.min(highlight.value + 1, navigable.value.length - 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    highlight.value = Math.max(highlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const value = navigable.value[highlight.value];
    if (value !== undefined) selectValue(value);
  } else if (e.key === 'Backspace') {
    search.value = search.value.slice(0, -1);
  } else if (e.key === 'Escape') {
    close();
  } else if (e.key.length === 1) {
    search.value += e.key;
    highlight.value = 0;
  }
}

watch(open, isOpen => {
  if (isOpen) window.addEventListener('keydown', handleKeydown);
  else window.removeEventListener('keydown', handleKeydown);
});

watch([navigable, search], () => {
  highlight.value = 0;
});

onUnmounted(() => window.removeEventListener('keydown', handleKeydown));

const clearText = computed(
  () => props.clearLabel || t('KANBAN.AUTOMATIONS.ANY')
);
</script>

<template>
  <div class="relative flex items-center">
    <button
      ref="triggerRef"
      type="button"
      class="flex items-center justify-between gap-2 border rounded-lg focus:outline-none transition-colors"
      :class="[
        compact ? '' : 'min-w-[140px]',
        small ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-sm',
        open
          ? 'border-n-brand text-n-brand bg-n-surface-2'
          : 'border-n-weak text-n-slate-10 bg-n-surface-2',
      ]"
      @click="toggleOpen"
    >
      <!-- chips coloridos quando múltipla seleção de tags (com cor) -->
      <span
        v-if="multiple && !search && selectedOptions.length"
        class="flex flex-wrap items-center gap-1"
      >
        <span
          v-for="opt in selectedOptions"
          :key="opt.value"
          class="rounded-md px-1.5 py-0.5 text-xs font-medium"
          :class="opt.color ? '' : 'bg-n-alpha-2 text-n-slate-12'"
          :style="chipStyle(opt.color)"
        >
          {{ opt.label }}
        </span>
      </span>
      <span v-else class="flex items-center gap-1.5 min-w-0">
        <span
          v-if="!search && selectedSingleColor"
          class="size-2.5 rounded-full shrink-0"
          :style="{ backgroundColor: selectedSingleColor }"
        />
        <span :class="isPlaceholder ? 'text-n-slate-10' : ''" class="truncate">
          {{ search || triggerLabel }}
        </span>
      </span>
      <span
        class="text-n-slate-10 shrink-0"
        :class="
          small
            ? 'i-lucide-chevron-down size-3'
            : 'i-lucide-chevron-down size-3.5'
        "
      />
    </button>

    <Teleport to="body">
      <template v-if="open">
        <div class="fixed inset-0 z-[10019]" @click="close" />
        <div
          class="w-max max-w-xs bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-64 overflow-y-auto"
          :style="dropdownStyle"
          @click.stop
        >
          <div
            v-if="clearable && !search"
            class="flex items-center px-3 py-2 text-sm cursor-pointer transition-colors"
            :class="
              highlight === 0
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : 'hover:bg-n-alpha-1 text-n-slate-12'
            "
            @click="selectValue('__clear__')"
          >
            {{ clearText }}
          </div>

          <div
            v-for="option in filteredOptions"
            :key="option.value"
            class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors"
            :class="[
              navigable[highlight] === option.value
                ? 'bg-n-alpha-1 text-n-brand font-medium'
                : 'hover:bg-n-alpha-1',
              isSelected(option.value) && navigable[highlight] !== option.value
                ? 'text-n-brand font-medium'
                : '',
              navigable[highlight] === option.value ? '' : 'text-n-slate-12',
            ]"
            @click="selectValue(option.value)"
          >
            <span
              v-if="option.icon"
              class="size-3.5 shrink-0"
              :class="[option.icon]"
            />
            <span
              v-if="option.dot"
              class="i-lucide-circle size-2 shrink-0"
              :class="option.dot"
            />
            <span
              v-if="option.color"
              class="size-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: option.color }"
            />
            <span class="flex-1 truncate">{{ option.label }}</span>
            <span
              v-if="multiple && isSelected(option.value)"
              class="i-lucide-check size-3 text-n-brand ml-auto shrink-0"
            />
          </div>

          <p
            v-if="filteredOptions.length === 0"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ $t('KANBAN.AUTOMATIONS.NOTHING_FOUND') }}
          </p>
        </div>
      </template>
    </Teleport>
  </div>
</template>
